import React from 'react';
import '../posters/poster_wizard/poster_wizard.css';
import WizardErrorCorrectionPreviewForm from './poster_wizard/wizard_error_correction_preview_form';
import WizardPreviewMapForm from './poster_wizard/country_map/wizard_preview_map_form';

export default function PosterPreview({ poster, isCompact = false, onClick }) {
    // Check if this is Error Correction type
    const isErrorCorrection = poster?.type === 'Error Correction';

    // Check if this is Country Map type
    const isCountryMap = poster?.type === 'Country Map';

    // If Error Correction, use the Error Correction preview component
    if (isErrorCorrection) {
        const containerClass = isCompact ? 'poster-preview-compact' : 'poster-preview-full';
        return (
            <div className={containerClass} onClick={onClick} style={onClick ? { cursor: 'pointer' } : {}}>
                <WizardErrorCorrectionPreviewForm
                    feedbackSourceData={poster.feedbackSourceData || {}}
                    feedbackTargetData={poster.feedbackTargetData || {}}
                    principleData={{ selectedPrinciple: poster.selected_principle || 'ECF' }}
                    isCompact={isCompact}
                />
            </div>
        );
    }

    // If Country Map, use the Country Map preview component
    if (isCountryMap) {
        // Transform poster data to match preview component format
        // Handle both API format (person_name, business_name, etc.) and wizard format (countryData, detailsData, etc.)
        let countryData = poster.countryData;
        let detailsData = poster.detailsData;
        let preferencesData = poster.preferencesData;

        // If data is in API format, transform it to wizard format
        if (!countryData && (poster.country || poster.person_name)) {
            countryData = {
                selectedCountry: poster.country || 'usa_mainland'
            };
            detailsData = {
                personName: poster.person_name || '',
                businessName: poster.business_name || '',
                personFunction: poster.person_function || '',
                functionAtLocation: poster.function_at_location || '',
                personImage: poster.person_image_path || null,
                personImageFileName: null,
                functionImage: poster.function_image_path || null,
                functionImageFileName: null,
            };
            preferencesData = {
                selectedPreferences: [
                    ...(poster.execution_in_given_set ? ['show_function_execution_given_set'] : []),
                    ...(poster.execution_in_green ? ['show_function_execution_green'] : [])
                ]
            };
        }

        const containerClass = isCompact ? 'poster-preview-compact' : 'poster-preview-full';
        return (
            <div className={containerClass} onClick={onClick} style={onClick ? { cursor: 'pointer' } : {}}>
                <WizardPreviewMapForm
                    countryData={countryData || {}}
                    detailsData={detailsData || {}}
                    preferencesData={preferencesData || {}}
                    isCompact={isCompact}
                />
            </div>
        );
    }

    // Get people from poster data
    const people = poster?.people || [];
    const selectedPrinciple = poster?.selected_principle || 'The Given Set';
    const showGivenToSelected = poster?.showGivenToSelected || false;
    const showGivesToSelected = poster?.showGivesToSelected || false;

    // Determine labels based on selection
    const mainLabel = showGivesToSelected ? 'Gives' : (showGivenToSelected ? 'Given' : '');
    const branchLabel = 'To';

    // Helper to get image source - handles both base64 (from wizard) and URLs (from API)
    const getImageSrc = (imageData, fileName) => {
        if (!imageData) return null;

        // If it's already a URL (starts with http), return it as is
        if (typeof imageData === 'string' && (imageData.startsWith('http') || imageData.startsWith('/'))) {
            return imageData;
        }

        // Otherwise, treat it as base64
        try {
            if (fileName) {
                const fileExtension = fileName.split('.').pop()?.toLowerCase();
                if (fileExtension === 'pdf') {
                    return `data:application/pdf;base64,${imageData}`;
                } else if (['jpg', 'jpeg'].includes(fileExtension)) {
                    return `data:image/jpeg;base64,${imageData}`;
                } else if (fileExtension === 'png') {
                    return `data:image/png;base64,${imageData}`;
                }
            }
            return `data:image/jpeg;base64,${imageData}`;
        } catch (e) {
            return null;
        }
    };

    // Helper function to strip HTML tags and get plain text
    const stripHtml = (html) => {
        if (!html) return '';
        const tmp = document.createElement('DIV');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    };

    // Check if we should show actual principles instead of the principle name
    const isShowActualPrinciple = selectedPrinciple === 'Show Actual Principle';
    const isShowDerivedPrinciple = selectedPrinciple === 'Show Derived Principle';
    
    // Get actual principles - handle both API format (actual_principles as JSON string/array) and wizard format
    let selectedActualPrinciples = [];
    if (poster?.actual_principles) {
        if (typeof poster.actual_principles === 'string') {
            try {
                selectedActualPrinciples = JSON.parse(poster.actual_principles);
            } catch (e) {
                // If parsing fails, treat as single string
                selectedActualPrinciples = [poster.actual_principles];
            }
        } else if (Array.isArray(poster.actual_principles)) {
            selectedActualPrinciples = poster.actual_principles;
        }
    } else if (poster?.actualPrinciplesData?.selectedActualPrinciples) {
        selectedActualPrinciples = poster.actualPrinciplesData.selectedActualPrinciples;
    }

    // Get typed principle text for "Show Derived Principle"
    const typedPrincipleText = poster?.principle_text ? stripHtml(poster.principle_text).trim() : 
                               (poster?.customizationData?.principleText ? stripHtml(poster.customizationData.principleText).trim() : '');

    // For "Show Derived Principle", show actual principles + typed text at the bottom
    const getDisplayPrinciples = () => {
        if (isShowDerivedPrinciple) {
            const principles = [...selectedActualPrinciples];
            // Add the typed text from the "Type Principle" step at the bottom if it exists
            if (typedPrincipleText) {
                principles.push(typedPrincipleText);
            }
            return principles;
        } else if (isShowActualPrinciple) {
            return selectedActualPrinciples;
        }
        return [];
    };

    const displayPrinciples = getDisplayPrinciples();
    // Only show principles list if there's at least one item to display
    const shouldShowPrinciplesList = (isShowActualPrinciple && selectedActualPrinciples.length > 0) || 
                                     (isShowDerivedPrinciple && displayPrinciples.length > 0);

    // For compact view, limit the number of people shown
    const displayPeople = isCompact ? people.slice(0, 4) : people;

    const containerClass = isCompact ? 'poster-preview-compact' : 'poster-preview-full';
    const visualClass = isCompact ? 'preview-set-visual-compact' : 'preview-set-visual';

    return (
        <div className={containerClass} onClick={onClick} style={onClick ? { cursor: 'pointer' } : {}}>
            <div className={visualClass}>
                <div className="given-set-container">
                    <div className="given-set-dot"></div>
                    <div className="given-set-box">
                        {shouldShowPrinciplesList ? (
                            <div className="given-set-actual-principles-list">
                                {displayPrinciples.map((principle, index) => (
                                    <div key={index} className="given-set-actual-principle-item">
                                        <span className="given-set-principle-number">{index + 1}.</span>
                                        <span className="given-set-principle-text">{principle}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <h3 className="given-set-title">{selectedPrinciple}</h3>
                        )}
                    </div>
                </div>

                {displayPeople.length > 0 && (
                    <div className="flowchart-container">
                        {/* Vertical line from box */}
                        <div className="vertical-line">
                            {mainLabel && <span className="line-label-main">{mainLabel}</span>}
                        </div>

                        {/* Wrapper for horizontal line and branches to ensure same width */}
                        <div className="flowchart-horizontal-wrapper">
                            {/* Horizontal connector line */}
                            <div className="horizontal-line"></div>

                            {/* Branch lines and profiles */}
                            <div className="flowchart-branches">
                                {displayPeople.map((person, index) => {
                                    // Handle both API format (with image_path) and wizard format (with image)
                                    const imageData = person.image_path || person.image;
                                    const fileName = person.fileName || person.title || '';
                                    const imageSrc = getImageSrc(imageData, fileName);
                                    const isPdf = fileName?.toLowerCase().endsWith('.pdf') || imageData?.toLowerCase().includes('.pdf');

                                    return (
                                        <div key={index} className="flowchart-branch">
                                            <div className="branch-vertical-line">
                                                {(showGivenToSelected || showGivesToSelected) && (
                                                    <span className="line-label-branch">{branchLabel}</span>
                                                )}
                                                <div className="branch-arrow"></div>
                                            </div>
                                            <div className="person-profile">
                                                {imageSrc ? (
                                                    isPdf ? (
                                                        <div className="profile-pdf">
                                                            <span>PDF</span>
                                                        </div>
                                                    ) : (
                                                        <img
                                                            src={imageSrc}
                                                            alt={person.name || `Person ${index + 1}`}
                                                            className="profile-image"
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                const placeholder = e.target.parentElement.querySelector('.profile-placeholder-fallback');
                                                                if (placeholder) {
                                                                    placeholder.style.display = 'flex';
                                                                }
                                                            }}
                                                        />
                                                    )
                                                ) : null}
                                                {!imageSrc && (
                                                    <div className="profile-placeholder">
                                                        <span>No Image</span>
                                                    </div>
                                                )}
                                                <div className="profile-name">{person.name || `Person ${index + 1}`}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {displayPeople.length === 0 && !isCompact && (
                    <div className="no-people-message">
                        <p>No people added to this poster.</p>
                    </div>
                )}
            </div>
        </div>
    );
}

